import React, { useEffect, useMemo, useState } from 'react';
import { useCheckAuthorityType, useCheckUser } from '../../../../hooks/useCheckUser';
import { useUserContext } from '../../../context/UserContext';
import { useCartState } from '../../../../contexts/cart';
import DelinquentAccountModal from '../DelinquentAccountModal/DelinquentAccountModal';
import './alertbanner.scss';
import config from '../../../../components/App/config';
import { SET_SHOW_DELINQUENT_ACCOUNT_MODAL } from '../../../actions/constants';
import { AUTHORITY_TYPE, EMPLOYEE, I2P_BANNER_IDENTIFIER } from '../../../../components/global/constants';
import {
    convertPhoneNumberAsPerSbmChannel,
    getSbmChannelPhoneNumbers,
    isAccountBlocked,
    isAccountClosed,
    isAlternateCID,
    reloadOnAccountChange
} from '../../../../components/global/utils/commonUtils';
import { isValidString } from '../../../../components/global/utils/logger';
import { USER_TYPE } from '../../../../constants/userDetailsConstants';
import Banner from './Banner';

const STATUS_TYPE = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    HOLD: 'hold'
};

const AlertBanner = () => {
    const authorityType = useCheckAuthorityType();
    const isP2PUser = authorityType === AUTHORITY_TYPE.P2P;

    const bannerEl = useMemo(() => document.querySelector('.alert__banner'), []);
    const notificationTitle = isValidString(bannerEl?.dataset?.title)
        ? bannerEl?.dataset?.title
        : 'There\'s an issue with your account';
    const notificationInactiveDescription = isValidString(bannerEl?.dataset?.descriptionblocked)
        ? bannerEl?.dataset?.descriptionblocked
        : `New ${isP2PUser ? 'Quotes' : 'reservations'} cannot be created. For more information, contact us at: `;
    const unformattedNotificationPhone = isValidString(bannerEl?.dataset?.phonenumber)
        ? bannerEl?.dataset?.phonenumber
        : '800-508-4756'
    const notificationPhone = convertPhoneNumberAsPerSbmChannel(unformattedNotificationPhone);
    const companyDetails = bannerEl?.dataset?.companydetails;
    const [type, setType] = useState(STATUS_TYPE.ACTIVE);
    const [{ userAccount, showDelinquentModal }, dispatch] = useCartState();
    const [showDelinqModal, setShowDelinqModal] = useState(showDelinquentModal);
    const [userState, { logoutUser }] = useUserContext();
    const userType = useCheckUser();
    const { userProfile, hasAccountChanged, isProfileLoaded } = userState;
    const companyID = sessionStorage.getItem("alternateCompanyIdValue");
    
    useEffect(() => {
        if (isProfileLoaded) {
            if (isAccountClosed(userAccount)) {
                setType(STATUS_TYPE.INACTIVE);
            } else {
                setType(STATUS_TYPE.ACTIVE);
            }
        }
    }, [isProfileLoaded, userAccount.accountNumber]);

    useEffect(() => {
        setShowDelinqModal(showDelinquentModal);
    }, [showDelinquentModal]);

    const isInvoicesPage = () => {
        const path = window?.location?.href ?? '';
        return path.includes('/myaccount/invoices');
    };

    const isEmployee = useMemo(() => {
        return userProfile?.type == EMPLOYEE;
    }, [userProfile]);

    const isMultipleAccounts = () => {
        if (userProfile?.accounts?.length > 1 || isEmployee) {
            return true;
        }
        return false;
    };
    const onDelinquentModalClose = () => {
        if (isAccountBlocked(userAccount)) {
            logoutUser();
        }
        if (hasAccountChanged) {
            reloadOnAccountChange(userType);
        }
        dispatch({
            type: SET_SHOW_DELINQUENT_ACCOUNT_MODAL,
            showDelinquentModal: false
        });
        setShowDelinqModal(false);
    };
    const isCloseIconRequired = () => {
        if (!isMultipleAccounts() || isAccountClosed(userAccount)) {
            return true;
        }
        return false;
    };
    const delinquentBannerClass = {
        hold: 'delinquentbanner--hold',
        inactive: 'delinquentbanner--inactive'
    };
    const renderDelinquentModal = () => {
        if (userType === USER_TYPE.CREDIT && !window.location.href.includes(config.pagePaths.signInHandler())) {
            return (
                <DelinquentAccountModal
                    isOpen={showDelinqModal}
                    showCTA={isMultipleAccounts()}
                    onRequestClose={onDelinquentModalClose}
                    showCloseIcon={isCloseIconRequired()}
                />
            );
        }
        return null;
    };

    const isEveryAccountDelinquent =
        userProfile?.accounts?.length > 1 && userProfile?.accounts?.every(acc => isAccountClosed(acc)); // return true if accounts more than 1 and all of them are inactive staus
    if (isP2PUser && (userProfile?.accounts?.length === 0 || isEveryAccountDelinquent) && isProfileLoaded) {
        return <DelinquentAccountModal isOpen={true} showCTA={false} onRequestClose={() => {}} showCloseIcon={false} />;
    }
    const parsedCompanyDetails = useMemo(() => {
        if (!companyDetails) return null;
        try {
            return JSON.parse(companyDetails);
        } catch {
            return null;
        }
    }, [companyDetails]);

    let companyData = null;

    if (userType === USER_TYPE.CREDIT && isAlternateCID()) {
        if (Array.isArray(parsedCompanyDetails)) {
            companyData = parsedCompanyDetails?.find(item => item?.companyId === companyID) || null;
        }
    }

    const i2pBannerData = useMemo(() => {
        if (!companyDetails) return null;

        try {
            const parsedData = JSON.parse(companyDetails);
            if (Array.isArray(parsedData)) {
                return parsedData?.find(item => item?.userType === I2P_BANNER_IDENTIFIER) || null;
            }
            return null;
        } catch {
            return null;
        }
    }, [companyDetails]);


    const i2pBannerMessage = useMemo(() => {
        if (i2pBannerData) {
            return i2pBannerData?.message;
        }
        return null;
    }, [i2pBannerData]);

    const showI2PBanner = useMemo(() => {
        // The banner is only visible to logged-in users who are identified as employees.
        // The banner appears on the "Invoices" page (/myaccount/invoices/) within the MyAccount section.
        return isEmployee && isInvoicesPage() && (
            i2pBannerData?.enableBanner === 'true' || i2pBannerData?.enableBanner === true
        );
    }, [isEmployee, i2pBannerData]);


    const showCompanyBanner = useMemo(() => {
        return companyData?.enableBanner === 'true' || companyData?.enableBanner === true;
    }, [companyData]);

    const companyBannerMessage = useMemo(() => {
        if (!showCompanyBanner || !companyData?.message) {
            return '';
        }
        return companyData?.message?.includes('#') 
            ? companyData.message.replace(/#/g, getSbmChannelPhoneNumbers()?.DEFAULT) 
            : companyData?.message;
    }, [companyData, showCompanyBanner]);

    return (
        <>
            {renderDelinquentModal()}
            {showI2PBanner && (
                <Banner message={i2pBannerMessage} className={delinquentBannerClass['inactive']} />
            )}
            {showCompanyBanner && (
                <Banner message={companyBannerMessage} className={delinquentBannerClass['inactive']} />
            )}
            {type === STATUS_TYPE.INACTIVE  && (
                <Banner
                    title={notificationTitle}
                    message={notificationInactiveDescription}
                    phone={notificationPhone}
                    className={delinquentBannerClass[type]}
                />
            )}
        </>
    );
};

export default React.memo(AlertBanner);
